import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import PasswordLink from '../components/PasswordLink';
import PasswordChange from '../components/PasswordChange';
import CalendarFeed from '../components/CalendarFeed';
import { User, Save, Shield, Palette, Calendar, Bell } from 'lucide-react';
import Button from '../components/Button';
import ThemeSelector from '../components/ThemeSelector';
import NotificationSettings from '../components/NotificationSettings';
import { NTRP_RATINGS, formatNtrpRating } from '../utils/ntrpRatings';
import { useSearchParams } from 'react-router-dom';

type ProfileSection = 'personal' | 'security' | 'appearance' | 'calendar' | 'notifications';

interface ActiveTeam {
  team_id: number;
  team_name: string;
  league_start_date: string | null;
  league_end_date: string | null;
}

interface SubscriptionInfo {
  max_active_teams: number;
  active_teams_count: number;
  remaining_capacity: number;
  is_active: boolean;
  active_teams: ActiveTeam[];
  subscription_source?: string | null;
  billing_cycle?: string | null;
  stripe_cancel_at_period_end?: boolean | null;
}

interface ProfileData {
  id: number;
  email: string;
  name: string;
  role: string;
  gender: string | null;
  ntrp_rating: number | null;
  tennisrecord_name?: string | null;
  phone?: string | null;
  picture_url?: string;
  subscription_info?: SubscriptionInfo;
}

const ProfilePage: React.FC = () => {
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [ntrpRating, setNtrpRating] = useState<number | null>(null);
  const [tennisrecordName, setTennisrecordName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [searchParams] = useSearchParams();

  // Tab navigation state with localStorage persistence
  // Helper function to get initial section: URL param takes precedence, then localStorage, then default
  const getInitialSection = (): ProfileSection => {
    const urlSection = searchParams.get('section');
    
    // URL parameter takes precedence
    if (urlSection && ['personal', 'security', 'appearance', 'calendar', 'notifications'].includes(urlSection)) {
      return urlSection as ProfileSection;
    }
    
    // Fall back to localStorage
    const stored = localStorage.getItem('profileActiveSection');
    if (stored && ['personal', 'security', 'appearance', 'calendar', 'notifications'].includes(stored)) {
      return stored as ProfileSection;
    }
    
    return 'personal';
  };

  const [activeSection, setActiveSection] = useState<ProfileSection>(getInitialSection);

  // Watch for URL parameter changes and update activeSection accordingly
  useEffect(() => {
    const urlSection = searchParams.get('section');
    
    if (urlSection && ['personal', 'security', 'appearance', 'calendar', 'notifications'].includes(urlSection)) {
      if (activeSection !== urlSection) {
        setActiveSection(urlSection as ProfileSection);
      }
    }
  }, [searchParams, activeSection]);

  useEffect(() => {
    if (activeSection) {
      localStorage.setItem('profileActiveSection', activeSection);
    }
  }, [activeSection]);

  const fetchProfile = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/users/me/profile');
      setProfileData(response.data);
      setDisplayName(response.data.name || '');
      setGender(response.data.gender || '');
      setNtrpRating(response.data.ntrp_rating || null);
      setTennisrecordName(response.data.tennisrecord_name || '');
      setPhone(response.data.phone || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
      showError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      const trimmedName = (displayName || '').trim();
      if (!trimmedName) {
        showError('Display name cannot be empty');
        setIsSaving(false);
        return;
      }

      await axios.patch('/users/me/profile', {
        name: trimmedName,
        gender: gender || null,
        ntrp_rating: ntrpRating || null,
        tennisrecord_name: (tennisrecordName || '').trim() || null,
        phone: (phone || '').trim() || null
      });
      
      // Optimistically update local state instead of refetching
      if (profileData) {
        setProfileData({
          ...profileData,
          name: trimmedName,
          gender: gender || null,
          ntrp_rating: ntrpRating || null,
          tennisrecord_name: (tennisrecordName || '').trim() || null,
          phone: (phone || '').trim() || null
        });
      }
      
      success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Failed to update profile');
      // On error, refetch to ensure we have the correct state
      fetchProfile();
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordLinkSuccess = () => {
    success('Password linked successfully! You can now sign in with email and password.');
  };

  const handlePasswordChangeSuccess = () => {
    success('Password changed successfully!');
  };


  if (isLoading) {
    return (
      <div>
        <Navigation />
        <LoadingSpinner />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div>
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600 dark:text-gray-400">Failed to load profile</div>
        </div>
      </div>
    );
  }

  // Tab navigation items
  const tabItems: Array<{ id: ProfileSection; label: string; icon: React.ReactNode }> = [
    { id: 'personal', label: 'Personal Details', icon: <User className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'appearance', label: 'Appearance', icon: <Palette className="w-4 h-4" /> },
    { id: 'calendar', label: 'Calendar Feeds', icon: <Calendar className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
  ];

  return (
    <div>
      <Navigation />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">My Profile</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your account settings and preferences</p>
          </div>

          {/* Profile Header Card - Always visible at top */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 sm:p-6 mb-6">
            <div className="flex items-center space-x-4 sm:space-x-6">
              {/* Profile Picture */}
              <div className="flex-shrink-0">
                {profileData.picture_url ? (
                  <img
                    src={profileData.picture_url}
                    alt={profileData.name}
                    className="w-14 h-14 sm:w-20 sm:h-20 rounded-full border-2 border-gray-200 dark:border-gray-700"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-primary-600 flex items-center justify-center text-white text-xl sm:text-2xl font-medium border-2 border-gray-200 dark:border-gray-700">
                    {profileData.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Basic Information */}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5 sm:mb-1 truncate">{profileData.name}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 truncate">{profileData.email}</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                  {profileData.role}
                </span>
              </div>
            </div>
          </div>

          {/* Horizontal Tabs */}
          <div className="border-b border-gray-100 dark:border-gray-700/50 mb-4 md:mb-6 overflow-x-auto overflow-y-hidden">
            <nav className="-mb-px flex space-x-4 md:space-x-8 min-w-max" aria-label="Profile sections">
              {tabItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`${
                    activeSection === item.id
                      ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  } whitespace-nowrap py-2 md:py-4 px-1 border-b-2 font-medium text-sm flex items-center min-h-[44px] transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                  aria-current={activeSection === item.id ? 'page' : undefined}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div>
            {/* Personal Details Section */}
            {activeSection === 'personal' && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Personal Details</h2>
                  </div>
                  <div className="space-y-8">
                    {/* Identity */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">Identity</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Name <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="displayName"
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className="block w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                            placeholder="Your name"
                            required
                          />
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            How your name appears throughout the app
                          </p>
                        </div>
                        <div>
                          <label htmlFor="trname" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Alias <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                          </label>
                          <input
                            id="trname"
                            type="text"
                            value={tennisrecordName}
                            onChange={(e) => setTennisrecordName(e.target.value)}
                            className="block w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                            placeholder="Alternative name or alias"
                          />
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            An alternative name or alias
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Tennis Profile */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">Tennis Profile</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Gender
                          </label>
                          <select
                            id="gender"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                            className="block w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                          >
                            <option value="">Not specified</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="ntrpRating" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            NTRP Rating
                          </label>
                          <select
                            id="ntrpRating"
                            value={ntrpRating || ''}
                            onChange={(e) => setNtrpRating(e.target.value === '' ? null : parseFloat(e.target.value))}
                            className="block w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                          >
                            <option value="">Not set</option>
                            {NTRP_RATINGS.map((rating) => (
                              <option key={rating} value={rating}>
                                {formatNtrpRating(rating)}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Your USTA rating level
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Contact */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">Contact</h3>
                      <div className="max-w-md">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Phone Number
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="block w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                          placeholder="(555) 123-4567"
                        />
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Visible to your teammates in the directory
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700/50">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={isSaving}
                        variant="primary"
                        size="lg"
                      >
                        {isSaving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Security</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage how you sign in to your account</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <PasswordLink
                      onSuccess={handlePasswordLinkSuccess}
                      onError={(error) => showError(error.message || 'Failed to link password')}
                    />
                    <PasswordChange
                      onSuccess={handlePasswordChangeSuccess}
                      onError={(error) => showError(error.message || 'Failed to change password')}
                    />
                  </div>
                </div>
            )}

            {/* Appearance Section */}
            {activeSection === 'appearance' && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Palette className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Appearance</h2>
                  </div>
                  <ThemeSelector />
                </div>
            )}

            {/* Calendar Feeds Section */}
            {activeSection === 'calendar' && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Calendar Feeds</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Subscribe to your matches in external calendar applications</p>
                    </div>
                  </div>
                  <CalendarFeed />
                </div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Control your email notification preferences</p>
                    </div>
                  </div>
                  <NotificationSettings />
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;


